import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  AuthService,
  Destroy,
  DialogService,
  IQueryResult,
  SearchConfig,
  SortExpression,
  tr,
  Types,
  KeyBase,
  Key,
  DialogResult,
  chainAndForkJoinObservables,
  SecureActionDialogData,
  sort,
} from '@kpd/framework-ngx';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LicenseService } from 'app/shared/services/license.service';
import { UserService } from 'app/shared/services/user.service';
import { License } from 'app/shared/models/license';
import { LicenseFacade } from 'app/shared/services/facades/license.facade';
import { Permissions } from 'app/shared/services/permissions';
import { TableController } from 'app/shared/utils/table-controller';
import { UsersNavigatorHelper } from 'app/users/users-navigator-helper';
import { User } from 'app/shared/models/user';

@Component({
  selector: 'app-license-details-users',
  templateUrl: './license-details-users.component.html',
})
export class LicenseDetailsUsersComponent extends TableController implements OnInit, OnChanges, OnDestroy {
  Permissions: typeof Permissions = Permissions;
  loading = false;
  @Input()
  license: License;
  @Output()
  switchMode = new EventEmitter();
  @Output()
  itemChanged = new EventEmitter<number>();
  @Destroy()
  destroy$: any;

  readOnlyMode = true;
  selectedKeys: Key<string>[] = [];
  selection = [];
  sortExpression: SortExpression;
  saveErrors: string[] = [];
  columnsConfig = this.createColumnsConfig();
  linkUsersSearchConfig = this.createLinkUsersSearchConfig();
  linkUsersCols = this.createColumnsConfig();
  linkUsersError: string | null = null;

  get isSystemUserSelected(): boolean {
    const systemUsers = (this.items as User[]).filter((u) => u.isSystemUser);
    return this.selectedKeys.some((k) => systemUsers.map((s) => s.key).includes(k));
  }

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  constructor(
    private userNavigationHelper: UsersNavigatorHelper,
    private licenseFacade: LicenseFacade,
    private licenseService: LicenseService,
    private userService: UserService,
    private dialogService: DialogService,
    private authService: AuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.authService.getIdentity().hasPermission(Permissions.UserLicenseUpdate)) {
      this.readOnlyMode = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.license) {
      this.selectedKeys = [];
      this.selection = [];
      this.saveErrors = [];
      if (!!changes.license.currentValue && !!changes.license.currentValue.key) {
        this.loadLicenseUsers(changes.license.currentValue.key);
      }
    }
  }

  loadLicenseUsers(licenseKey: Key<string>, licenseChanged: boolean = false): void {
    this.loading = true;
    this.licenseFacade.detailLoadingStarted();
    this.licenseService.getLicenseUsers(licenseKey).subscribe(
      (users) => {
        this.items = sort(users, { name: 'displayName' });
        this.license.quantityAssigned = this.items.length;
        this.itemChanged.emit(this.license.quantityAssigned);
        if (licenseChanged) {
          this.licenseFacade.licenseChanged(this.license);
        }
        this.licenseFacade.detailLoadingFinished();
        this.loading = false;
      },
      () => {
        this.licenseFacade.detailLoadingFinished();
        this.loading = false;
      },
    );
  }

  onAdd(): void {
    this.linkUsersError = null;
    this.licenseFacade.detailLoadingStarted();
    // load initial data
    this.userService
      .get()
      .pipe(
        switchMap((queryResult: IQueryResult) => {
          this.licenseFacade.detailLoadingFinished();
          const inputData: { queryResult: IQueryResult; keysToExclude: KeyBase[] } = {
            queryResult: queryResult,
            keysToExclude: this.items.map((user) => user.key),
          };
          return this.dialogService.open('linkEntities', inputData).afterClosed();
        }),
        switchMap((selectedKeys: any) => {
          if (!selectedKeys || this.license.isExpired) {
            return of(null);
          }
          this.saveErrors = [];
          this.licenseFacade.detailLoadingStarted();
          this.loading = true;
          // re-load resource suppliers after a successful add
          const observers = selectedKeys.map((key: Key<string>) => {
            return this.licenseService.createUserLicense(this.license.key, key.id).pipe(
              catchError(() => {
                this.saveErrors.push(tr('Following user could not be added: ') + key.id);
                return of(null);
              }),
            );
          });
          return chainAndForkJoinObservables(observers);
        }),
      )
      .subscribe(
        () => {
          this.licenseFacade.detailLoadingFinished();
          this.loadLicenseUsers(this.license.key, true);
          this.loading = false;
        },
        () => {
          this.licenseFacade.detailLoadingFinished();
          this.loading = false;
        },
      );
  }

  onDelete(): void {
    if (this.isSystemUserSelected) {
      const messages = [
        {
          text: tr('Are you sure you want to remove this license from a System User?'),
        },
      ];
      const confirmData: SecureActionDialogData = {
        header: tr(`Remove {1} from {2} ?`)
          .replace('{1}', `${tr('Role')} ${this.license.name}`)
          .replace('{2}', ` ${tr('System User')}`),
        messages: messages,
        cancelButtonText: tr('Cancel'),
        verifyButtonText: tr('Remove'),
        verifyButtonStyle: 'danger',
        verificationString: this.license.name.trim(),
        verificationLabel: tr('Type the name of the {value} to confirm').replace(/{.*}/, `${tr('license')}`),
      };
      this.dialogService
        .open('secureDelete', confirmData)
        .afterClosed()
        .subscribe((result: DialogResult) => {
          if (result === DialogResult.Verify) {
            this.executeUserLicenseDelete();
            return true;
          } else {
            return false;
          }
        });
      return;
    }

    this.dialogService
      .open('confirmLicenseUserDelete', {
        message: tr('Are you sure you want to unassign the license(s)?'),
        header: tr('Unassign license(s)'),
        verifyButtonText: tr('Unassign'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe((result: DialogResult) => {
        this.saveErrors = [];
        if (result === DialogResult.Verify) {
          this.executeUserLicenseDelete();
        }
      });
  }

  private executeUserLicenseDelete() {
    this.loading = true;
    this.licenseFacade.detailLoadingStarted();
    const observables = this.selectedKeys.map((key) => {
      return this.licenseService.deleteUserLicense(this.license.key, key.toString()).pipe(
        catchError(() => {
          this.saveErrors.push(tr('Following user could not be deleted: ') + key.id);
          this.licenseFacade.detailLoadingFinished();
          return of(null);
        }),
      );
    });
    chainAndForkJoinObservables(observables).subscribe(() => {
      this.loading = false;
      this.selectedKeys = [];
      this.loadLicenseUsers(this.license.key, true);
      this.licenseFacade.detailLoadingFinished();
    });
  }

  userSelectionChanged(event: KeyBase[]): void {
    if (event.length > this.license.quantityAvailable) {
      this.linkUsersError = tr('You selected more licenses than you can assign');
    } else {
      this.linkUsersError = null;
    }
  }

  isLicenseAvailable(): boolean {
    return this.license instanceof License;
  }

  createColumnsConfig(): any[] {
    return [
      { field: 'upn', header: tr('Upn'), width: '20%' },
      { field: 'firstName', header: tr('First name'), width: '20%' },
      { field: 'lastName', header: tr('Last name'), width: '20%' },
      { field: 'displayName', header: tr('Name'), width: '25%' },
      { field: 'lastLoginDate', header: tr('Last Login'), width: '15%', type: 'date' },
    ];
  }

  createLinkUsersSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'id',
          displayValue: tr('Upn'),
          dataType: Types.String,
        },
        {
          field: 'firstName',
          displayValue: tr('First name'),
          dataType: Types.String,
        },
        {
          field: 'lastName',
          displayValue: tr('Last name'),
          dataType: Types.String,
        },
        {
          field: 'displayName',
          displayValue: tr('Name'),
          dataType: Types.String,
        },
      ],
    });
  }

  navigateToUser(key: Key<string>): void {
    this.userNavigationHelper.navigateToDetail(key, false, false, false, { tab: 'general' });
  }

  ngOnDestroy(): void {}
}
